<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, ref, watch} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import StepBox from "@/components/google/partials/StepBox.vue";
import RadioBox from "@/components/google/partials/RadioBox.vue";
import RadioBoxGrid from "@/components/google/partials/RadioBoxGrid.vue";

const props = defineProps({
  consolesJson: {
    type: String,
    required: true
  },
  email: {
    type: String,
    required: true,
  },
  websiteUrl: {
    type: String,
    required: true,
  },
  connectedSelectionJson: {
    type: String,
    default: 'null',
  },
});

const selectedConsoleId = ref();
const consoles = computed(() => JSON.parse(props.consolesJson));
const connectedSelection = computed(() => JSON.parse(props.connectedSelectionJson));

watch(consoles, () => {
  if (selectedConsoleId.value) {
    return;
  }

  const connectedSiteUrl = connectedSelection.value?.siteUrl;
  if (connectedSiteUrl) {
    const connectedConsole = consoles.value.find((console) => console.siteUrl === connectedSiteUrl);
    if (connectedConsole) {
      selectedConsoleId.value = connectedConsole.siteUrl;
      return;
    }
  }

  if (consoles.value.length === 1) {
    selectedConsoleId.value = consoles.value[0].siteUrl;
  }
}, {
  immediate: true,
});

const {mutate: submit, isPending, isSuccess} = useMutation({
  mutationFn: () => axios.post('/ajax/v2/google/console', {
    siteUrl: selectedConsoleId.value,
  }),
  onSuccess: ({data}) => {
    successToast('Google Search Console connected successfully, redirecting...');
    window.setTimeout(() => {
      window.location.href = data.redirect;
    }, 2000)

  },
  onError: () => {
    errorToast('Something went wrong, please try again');
  },
})

</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-y-8 tw-px-4 md:tw-px-0">
    <div>
      <h1 class="tw-font-bold tw-text-3xl md:tw-text-4.5xl">
        Connect Google Search Console
      </h1>
      <p class="tw-text-lg">
        Signed in as <span class="tw-font-bold">{{ email }}</span>, please choose what to link to <span
          class="tw-font-bold"
        >{{ websiteUrl }}</span>
      </p>
    </div>
    <StepBox
      :step="1"
      title="Choose console account"
      :active="!selectedConsoleId"
    >
      <template #subtitle>
        <p>
          Choose which Google Search Console account you would like to connect:
        </p>
      </template>

      <template #default>
        <RadioBoxGrid>
          <RadioBox
            v-for="console in consoles"
            :key="console.id"
            v-model="selectedConsoleId"
            :title="console.displayName"
            :value="console.siteUrl"
            :already-connected="connectedSelection?.siteUrl === console.siteUrl"
          />
        </RadioBoxGrid>
        <div class="tw-flex tw-flex-col-reverse sm:tw-flex-row tw-gap-2 tw-mt-8">
          <DefaultButton
            as="a"
            href="/google/oauth/cancel"
            class="sm:tw-mr-auto"
            variant="base"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            class="sm:tw-ml-auto"
            :disabled="!selectedConsoleId || isPending || isSuccess"
            :loading="isPending"
            @click="submit"
          >
            Connect
          </DefaultButton>
        </div>
      </template>
    </StepBox>
  </div>
</template>